import { useTracker, useUBLContext, ZigzagEvent } from '@common/log-manager';

import { ADD_TO_CART_COUNT_SESSION_DATA_KEY } from '../constant/key';
import { AddCartLogContext, PurchaseLogContext } from '../core/confirm-button.core.type';
import { LogConfig } from '../type/common.type';
import { addZzSessionData, getZzSessionData } from '../util/executors/zz-session-cookies-executor';
import { useExternalToolLog } from './external-tool-log/useExternalToolLog';

/**
 * 기본 로그들을 자동으로 처리하고, 필요시 커스터마이징 가능
 */
export const useConfirmButtonLog = () => {
  const tracker = useTracker();
  const ublContext = useUBLContext();
  const externalToolLog = useExternalToolLog();
  const navigation = ublContext.navigation || 'zpay_option';

  const sendAddToCartLogs = (context: AddCartLogContext, config?: LogConfig) => {
    const { disableLogs = false, disableUBL = false, disableBraze = false, disableGA4 = false } = config || {};

    if (disableLogs) {
      return;
    }

    const {
      catalogProduct,
      catalogProductId,
      finalPrice,
      matchingCatalogProductId,
      confirmItems,
      sendGeneralProductId,
      sendZigzinProductId,
    } = context;

    // 1. UBL 로그
    if (!disableUBL) {
      ZigzagEvent.subl({
        category: 'add_to_cart',
        navigation,
        navigation_sub: JSON.stringify({ product_id: catalogProductId }),
        object_type: 'catalog',
        object_id: catalogProductId,
        object_section: 'bottom_bar',
        ...(((matchingCatalogProductId && sendGeneralProductId) || sendZigzinProductId) && {
          data: JSON.stringify({
            ...(sendGeneralProductId && { general_product_id: matchingCatalogProductId }),
            ...(sendZigzinProductId && { zonly_product_id: matchingCatalogProductId }),
          }),
        }),
      });
    }

    // 2. Braze 로그
    if (!disableBraze) {
      ZigzagEvent.logBraze('add_to_cart', {
        shop_name: catalogProduct?.shop_name ?? '',
        shop_domain_name: catalogProduct?.shop_main_domain ?? '',
        shop_id: catalogProduct?.shop_id ?? '',
        product_name: catalogProduct?.name ?? '',
        product_category_lv1: catalogProduct?.category_list?.[0]?.value ?? '',
        price: finalPrice,
        promotion_type: !!catalogProduct?.product_price?.first_order_discount?.promotion_id,
        product_category_lv2: catalogProduct?.managed_category_list
          ?.slice(0, 2)
          .map((o) => o.value)
          .join('/'),
      });

      // BRAZE LOG: 동 세션에서 카트담기 액션 3회 이상의 경우에 로그 발송
      const addToCartCountData: Record<string, number> = getZzSessionData(ADD_TO_CART_COUNT_SESSION_DATA_KEY);
      addToCartCountData.count = addToCartCountData.count ? Number(addToCartCountData.count) + 1 : 1;
      addZzSessionData({ name: ADD_TO_CART_COUNT_SESSION_DATA_KEY, value: addToCartCountData });

      if (addToCartCountData.count >= 3) {
        ZigzagEvent.logBraze('add_to_cart_product', {
          add_to_cart_count: addToCartCountData.count,
          add_to_cart_count_store: catalogProduct?.shop_name ?? '',
          shop_id: catalogProduct?.shop_id ?? '',
          catalog_product_id: catalogProductId,
        });
      }
    }

    // GA4 로그
    if (!disableGA4) {
      externalToolLog.addToCartEvent({
        managedCategoryList: catalogProduct?.managed_category_list ?? [],
        product_name: catalogProduct?.name ?? '',
        product_id: catalogProductId,
        price: finalPrice || 0,
        items: confirmItems.map((item) => ({
          item_id: catalogProductId,
          item_name: catalogProduct?.name ?? '',
          price: item.price,
          quantity: item.quantity,
        })),
        category: catalogProduct?.category_list?.[0]?.value ?? '',
        shop_id: catalogProduct?.shop_id + '',
        shop_name: catalogProduct?.shop_name ?? '',
      });
    }
  };

  const sendPurchaseLogs = (context: PurchaseLogContext, config?: LogConfig) => {
    const { disableLogs = false, disableUBL = false } = config || {};

    if (disableLogs) {
      return;
    }

    const { catalogProductId, matchingCatalogProductId, sendGeneralProductId, sendZigzinProductId } = context;

    if (!disableUBL) {
      if (context.isZigzagApp) {
        tracker.sendImmediately({
          category: 'click',
          navigation: 'zpay_option',
          object_type: 'button',
          object_id: 'checkout',
          data: { catalog_product_id: catalogProductId },
        });
      }
      ZigzagEvent.subl({
        category: 'click',
        navigation,
        object_id: 'checkout',
        object_type: 'button',
        data: JSON.stringify({
          catalog_product_id: catalogProductId,
          ...(matchingCatalogProductId && {
            ...(sendGeneralProductId && { general_product_id: matchingCatalogProductId }),
            ...(sendZigzinProductId && { zonly_product_id: matchingCatalogProductId }),
          }),
        }),
      });
    }
  };

  const sendPurchaseKakaoUBL = (catalog_product_id: string) => {
    tracker.sendImmediately({
      category: 'click',
      navigation: 'zpay_option',
      object_type: 'button',
      object_id: 'kakao_checkout',
      data: { catalog_product_id },
    });
  };

  return {
    sendAddToCartLogs,
    sendPurchaseLogs,
    sendPurchaseKakaoUBL,
  };
};
